'use client';

import { ReactNode } from 'react';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { css, stack } from '@bts-web/utils-style-engine';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  AssetImage,
  DialogBase,
  HyperLinkButtonBase,
  HyperLinkButtonBaseVariants,
} from '../../../common';
import { PoweredBy } from '../../../trade/Reusable/PoweredBy/PoweredBy';
import { ModalComponent } from '../ModalDrawerContext/ModalDrawerContext';
import { useRouter } from 'next/navigation';
import { RegularSwap } from '../../svgIcons';

const assetLogoStyles = css({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '100%',
  p: {
    base: 'extra_small_2',
    lg: 'small',
  },
  backgroundColor: 'neutrals.fill_quinary',
  boxShadow: `0px 0px 18px 0px rgba(0, 0, 0, 0.25)`,
});

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  mx: 'auto',
  gap: 'medium',
  pb: 'medium',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: '240px',
});

export type SuccessModalProps = {
  title: string;
  subtitle?: string;
  description?: string | ReactNode;
  poweredByText?: string;
  logo?: {
    url: string;
    name: string;
  };
  sourceLogo?: {
    url: string;
    name: string;
  };
  icon?: ReactNode;
  continueLink?: {
    label: string;
    url: string;
  };
  cancelLink?: {
    label: string;
    url: string;
  };
  goToPortfolioBtnVariant?: HyperLinkButtonBaseVariants;
  hidePortfolioButton?: boolean;
  closeIconShouldRedirectToPortfolio?: boolean;
  disableCloseButton?: boolean;
};

export const SuccessModal: ModalComponent<SuccessModalProps> = ({
  logo,
  sourceLogo,
  title,
  subtitle,
  description,
  poweredByText,
  onClose,
  disableCloseButton,
  icon,
  continueLink,
  cancelLink,
  goToPortfolioBtnVariant,
  hidePortfolioButton = false,
  closeIconShouldRedirectToPortfolio = false,
}) => {
  const router = useRouter();

  const { t } = useClientTranslation();

  const goToPortfolioText = cancelLink?.label || t('ok_go_to_portfolio');

  const portfolioUrl = cancelLink?.url || NEXT_ROUTES.PORTFOLIO;

  const { appId } = getAppConfig();

  const handleClose = () => {
    if (closeIconShouldRedirectToPortfolio) {
      router.push(NEXT_ROUTES.PORTFOLIO);
    }

    onClose();
  };

  return (
    <DialogBase
      id="transaction-success-modal"
      onClose={handleClose}
      size="small"
      onMobileFullScreen
      visual="secondary"
      disableCloseButton={disableCloseButton}
    >
      <section className={containerStyles}>
        {icon && (
          <div
            className={css({
              width: '52px',
              height: '52px',
              color: 'positive.text_primary',
              backgroundColor: 'positive.fill_secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              flexShrink: 0,
            })}
          >
            {icon}
          </div>
        )}
        <div
          className={css({
            display: 'flex',
            gap: 'extra_small',
            alignItems: 'center',
          })}
        >
          {sourceLogo?.url && (
            <>
              <div className={assetLogoStyles}>
                <AssetImage
                  src={sourceLogo?.url}
                  width={48}
                  height={48}
                  alt={sourceLogo?.name}
                  priority
                  testId="source-asset-logo"
                  aria-label="asset logo"
                />
              </div>
              <RegularSwap
                className={css({
                  color: 'neutrals.text_secondary',
                })}
              />
            </>
          )}

          {logo && (
            <div className={assetLogoStyles}>
              <AssetImage
                src={logo.url}
                width={48}
                height={48}
                alt={logo.name}
                priority
                testId="trade-asset-logo"
                aria-label="asset logo"
              />
            </div>
          )}
        </div>

        {title && (
          <h1
            className={css({
              fontSize: 'headline.medium',
              fontWeight: 'headline.large_semi_bold',
              lineHeight: 1.1,
            })}
          >
            {title}
          </h1>
        )}

        {subtitle && (
          <div
            className={css({
              textAlign: 'center',
              color: 'neutrals.text_primary',
              fontSize: 'headline.small',
              fontWeight: 'headline.small',
              letterSpacing: 'headline.small',
              lineHeight: 'headline.small',
            })}
          >
            {subtitle}
          </div>
        )}

        {description && (
          <div
            className={css({
              color: 'neutrals.text_secondary',
              fontSize: 'body.medium',
              fontWeight: 'body.medium',
              letterSpacing: 'body.medium',
              lineHeight: 'body.medium',
            })}
          >
            {description}
          </div>
        )}
      </section>

      <section
        className={stack({
          marginTop: 'auto',
          marginInlineStart: 'auto',
          marginInlineEnd: 'auto',
          maxWidth: '390px',
          width: '100%',
          gap: 'small',
          alignItems: 'center',
        })}
      >
        <PoweredBy
          centered
          noMargins
          value={poweredByText || t('powered_by_@tenant')}
          showLogo={!poweredByText}
        />
        {continueLink?.url && (
          <HyperLinkButtonBase
            to={continueLink.url}
            visual={appId === 4 ? 'accent' : 'primary'}
            prefetch
            data-testid="continue button"
            onClick={onClose}
          >
            {continueLink.label}
          </HyperLinkButtonBase>
        )}

        {hidePortfolioButton ? null : (
          <HyperLinkButtonBase
            to={portfolioUrl}
            visual={goToPortfolioBtnVariant || 'secondary'}
            prefetch
            date-testid="goToPortfolioButton"
            onClick={onClose}
          >
            {goToPortfolioText}
          </HyperLinkButtonBase>
        )}
      </section>
    </DialogBase>
  );
};
